import { ChangeDetectionStrategy, Component, computed, inject, model, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { FikoYTKokBilesenComponent } from '@/src/service/kok-bilesen/kok-bilesen.component';
import { HmiLookupService } from '../../../services/hmi-lookup.service';
import { FikoYTRequestService } from '@/src/app/shared/services/fikoyt-request.service';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';

const DURUM_SECENEKLERI = [
  { deger: 'bekliyor', etiket: 'Bekliyor', renk: '#faad14' },
  { deger: 'onaylandi', etiket: 'Onaylandı', renk: '#52c41a' },
  { deger: 'uretimde', etiket: 'Üretimde', renk: '#1890ff' },
  { deger: 'hazir', etiket: 'Hazır', renk: '#13c2c2' },
  { deger: 'sevk_edildi', etiket: 'Sevk Edildi', renk: '#722ed1' },
  { deger: 'iptal', etiket: 'İptal', renk: '#ff4d4f' },
];

@Component({
  selector: 'siparis-edit',
  standalone: true,
  imports: [FormsModule, NzInputModule, NzInputNumberModule, NzSelectModule, NzDatePickerModule,
    CdkTextareaAutosize],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="se">
      <div class="se-baslik">
        <div class="se-baslik-ikon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
            <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/>
          </svg>
        </div>
        <span>Sipariş Bilgileri</span>
      </div>

      <div class="se-form">
        <div class="form-grup">
          <label>Müşteri</label>
          <div class="input-wrap">
            <nz-select
              [ngModel]="p()?.musteri"
              (ngModelChange)="guncelle('musteri', $event)"
              nzPlaceHolder="Müşteri seçin..."
              nzAllowClear nzShowSearch
              style="width:100%"
              [nzCustomTemplate]="musteriTpl">
              @for (m of lookup.musteriler(); track m._id) {
                <nz-option [nzValue]="m.firmaAdi" [nzLabel]="m.firmaAdi" nzCustomContent>
                  <div class="sel-item"><span class="sel-emoji">🏪</span><div class="sel-bilgi"><span class="sel-ad">{{ m.firmaAdi }}</span>@if (m.yetkili) { <span class="sel-detay">{{ m.yetkili }}</span> }</div></div>
                </nz-option>
              }
            </nz-select>
            <ng-template #musteriTpl let-selected>
              <span class="sel-item"><span class="sel-emoji">🏪</span><span class="sel-ad">{{ selected.nzLabel }}</span></span>
            </ng-template>
            <div class="input-glow"></div>
          </div>
        </div>

        <div class="form-grup">
          <label>Başlık <span class="zorunlu">*</span></label>
          <div class="input-wrap">
            <input nz-input autocomplete="off"
              [ngModel]="p()?.urunAdi || ''"
              (ngModelChange)="guncelle('urunAdi', $event)"
              placeholder="Sipariş başlığı..."/>
            <div class="input-glow"></div>
          </div>
        </div>

        <div class="form-grup">
          <label>Proje</label>
          <div class="input-wrap">
            <nz-select [ngModel]="p()?.projeRef" (ngModelChange)="guncelle('projeRef', $event)"
              nzPlaceHolder="Proje bağla..." nzAllowClear nzShowSearch style="width:100%"
              (nzOnSearch)="projeAra($event)">
              @for (pr of projeler(); track pr._id) {
                <nz-option [nzValue]="pr.ad" [nzLabel]="pr.ad" nzCustomContent>
                  <div class="sel-item"><span class="sel-emoji">📂</span><span class="sel-ad">{{ pr.ad }}</span></div>
                </nz-option>
              }
            </nz-select>
            <div class="input-glow"></div>
          </div>
        </div>

        <div class="se-ayirici"></div>

        <div class="form-satir">
          <div class="form-grup" style="flex:1">
            <label>Miktar <span class="zorunlu">*</span></label>
            <div class="input-wrap">
              <nz-input-number
                [ngModel]="p()?.miktar || 1"
                (ngModelChange)="guncelle('miktar', $event)"
                [nzMin]="1" [nzStep]="1"
                style="width:100%">
              </nz-input-number>
              <div class="input-glow"></div>
            </div>
          </div>
          <div class="form-grup" style="flex:1">
            <label>Birim</label>
            <div class="input-wrap">
              <nz-select
                [ngModel]="p()?.birim || 'Adet'"
                (ngModelChange)="guncelle('birim', $event)"
                style="width:100%"
                [nzCustomTemplate]="birimTpl">
                @for (b of birimler; track b) {
                  <nz-option [nzValue]="b" [nzLabel]="b" nzCustomContent>
                    <div class="sel-item"><span class="sel-dot" style="background:#8b5cf6"></span><span class="sel-ad">{{ b }}</span></div>
                  </nz-option>
                }
              </nz-select>
              <ng-template #birimTpl let-selected>
                <span class="sel-item"><span class="sel-dot" style="background:#8b5cf6"></span><span class="sel-ad">{{ selected.nzLabel }}</span></span>
              </ng-template>
              <div class="input-glow"></div>
            </div>
          </div>
        </div>

        <div class="form-satir">
          <div class="form-grup" style="flex:1">
            <label>Birim Fiyat</label>
            <div class="input-wrap">
              <nz-input-number
                [ngModel]="p()?.birimFiyat || 0"
                (ngModelChange)="guncelle('birimFiyat', $event)"
                [nzMin]="0" [nzStep]="0.01"
                style="width:100%">
              </nz-input-number>
              <div class="input-glow"></div>
            </div>
          </div>
          <div class="form-grup" style="flex:1">
            <label>Para Birimi</label>
            <div class="input-wrap">
              <nz-select
                [ngModel]="p()?.paraBirimi || 'TRY'"
                (ngModelChange)="guncelle('paraBirimi', $event)"
                style="width:100%"
                [nzCustomTemplate]="pbTpl">
                <nz-option nzValue="TRY" nzLabel="TRY" nzCustomContent>
                  <div class="sel-item"><span class="sel-emoji">₺</span><span class="sel-ad">TRY - Türk Lirası</span></div>
                </nz-option>
                <nz-option nzValue="USD" nzLabel="USD" nzCustomContent>
                  <div class="sel-item"><span class="sel-emoji">$</span><span class="sel-ad">USD - Amerikan Doları</span></div>
                </nz-option>
                <nz-option nzValue="EUR" nzLabel="EUR" nzCustomContent>
                  <div class="sel-item"><span class="sel-emoji">€</span><span class="sel-ad">EUR - Euro</span></div>
                </nz-option>
              </nz-select>
              <ng-template #pbTpl let-selected>
                <span class="sel-item"><span class="sel-emoji">{{ selected?.nzValue === 'TRY' ? '₺' : selected?.nzValue === 'USD' ? '$' : '€' }}</span><span class="sel-ad">{{ selected?.nzLabel }}</span></span>
              </ng-template>
              <div class="input-glow"></div>
            </div>
          </div>
        </div>

        <div class="se-ayirici"></div>

        <div class="form-grup">
          <label>Teslim Tarihi</label>
          <div class="input-wrap">
            <nz-date-picker
              [ngModel]="p()?.teslimTarihi"
              (ngModelChange)="guncelle('teslimTarihi', $event)"
              nzPlaceHolder="Tarih seçin..."
              style="width:100%">
            </nz-date-picker>
            <div class="input-glow"></div>
          </div>
        </div>

        <div class="form-grup">
          <label>Durum</label>
          <div class="input-wrap">
            <nz-select
              [ngModel]="p()?.durum || 'bekliyor'"
              (ngModelChange)="guncelle('durum', $event)"
              style="width:100%"
              [nzCustomTemplate]="sipDurumTpl">
              @for (d of durumSecenekleri; track d.deger) {
                <nz-option [nzValue]="d.deger" nzCustomContent [nzLabel]="d.etiket">
                  <div class="sel-item"><span class="sel-dot" [style.background]="d.renk"></span><span class="sel-ad">{{ d.etiket }}</span></div>
                </nz-option>
              }
            </nz-select>
            <ng-template #sipDurumTpl let-selected>
              <span class="sel-item"><span class="sel-dot" [style.background]="sipDurumRenk(selected?.nzValue)"></span><span class="sel-ad">{{ selected?.nzLabel }}</span></span>
            </ng-template>
            <div class="input-glow"></div>
          </div>
        </div>

        <div class="se-ayirici"></div>

        <div class="form-grup">
          <label>Notlar</label>
          <div class="input-wrap">
            <textarea nz-input autocomplete="off" readonly onfocus="this.removeAttribute('readonly')"
              [ngModel]="p()?.notlar || ''"
              (ngModelChange)="guncelle('notlar', $event)"
              placeholder="Sipariş ile ilgili notlar..."
              cdkTextareaAutosize [cdkAutosizeMinRows]="2" [cdkAutosizeMaxRows]="4">
            </textarea>
            <div class="input-glow"></div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`

    :host { display: block; }

    .se {
      display: flex;
      flex-direction: column;
      gap: 0;
      padding: 0;
    }

    .se-baslik {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 12px 16px;
      margin-bottom: 4px;
      border-radius: 10px;
      background: linear-gradient(135deg, var(--py-arkaplan-soluk, rgba(0,0,0,0.03)), transparent);
    }

    .se-baslik-ikon {
      width: 32px;
      height: 32px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, #f97316, #fb923c);
      color: #fff;
      flex-shrink: 0;
      box-shadow: 0 2px 8px rgba(249, 115, 22, 0.35);
      position: relative;
    }

    .se-baslik-ikon::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: inherit;
      background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.2) 50%, transparent 100%);
      background-size: 200% 100%;
      animation: shimmer 3s infinite linear;
      pointer-events: none;
    }

    .se-baslik span {
      font-size: 13px;
      font-weight: 600;
      color: var(--py-metin, #333);
      letter-spacing: 0.3px;
    }

    .se-form {
      display: flex;
      flex-direction: column;
      gap: 14px;
      padding: 4px 2px;
    }

    .se-ayirici {
      height: 1px;
      background: linear-gradient(90deg, transparent, var(--py-kenarlik, #d9d9d9) 20%, var(--py-kenarlik, #d9d9d9) 80%, transparent);
      opacity: 0.4;
      margin: 2px 0;
    }

    .form-satir {
      display: flex;
      gap: 12px;
    }

    .form-grup {
      display: flex;
      flex-direction: column;
      gap: 5px;
    }

    .form-grup label {
      font-size: 11px;
      font-weight: 500;
      color: var(--py-metin-ucuncul, rgba(0,0,0,0.45));
      text-transform: uppercase;
      letter-spacing: 0.6px;
      padding-left: 2px;
      transition: color 0.2s ease;
    }

    .form-grup:focus-within label {
      color: var(--py-link, #1890ff);
    }

    .zorunlu {
      color: #ff4d4f;
      font-size: 13px;
      line-height: 1;
      vertical-align: middle;
    }

    .input-wrap {
      position: relative;
    }

    .input-glow {
      position: absolute;
      bottom: 0;
      left: 50%;
      width: 0;
      height: 2px;
      border-radius: 1px;
      background: linear-gradient(90deg, #f97316, #1890ff, #f97316);
      transition: width 0.35s cubic-bezier(0.4, 0, 0.2, 1),
                  left 0.35s cubic-bezier(0.4, 0, 0.2, 1),
                  opacity 0.3s ease;
      opacity: 0;
      pointer-events: none;
      z-index: 1;
    }

    .input-wrap:focus-within .input-glow {
      width: 100%;
      left: 0;
      opacity: 1;
    }

    .input-wrap input[nz-input],
    .input-wrap textarea[nz-input] {
      background: var(--py-arkaplan-soluk, rgba(0,0,0,0.02));
      border: 1px solid var(--py-kenarlik, #d9d9d9);
      border-radius: 8px;
      padding: 7px 11px;
      font-size: 13px;
      color: var(--py-metin, #333);
      transition: border-color 0.25s ease, background 0.25s ease, box-shadow 0.25s ease;
    }

    .input-wrap input[nz-input]:hover,
    .input-wrap textarea[nz-input]:hover {
      border-color: var(--py-kenarlik-hover, #b3b3b3);
      background: var(--py-arkaplan-hover, #f5f5f5);
    }

    .input-wrap input[nz-input]:focus,
    .input-wrap textarea[nz-input]:focus {
      border-color: var(--py-kenarlik-aktif, #1890ff);
      background: var(--py-input-arka, #fff);
      box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
      outline: none;
    }

    .input-wrap ::ng-deep .ant-select-selector {
      background: var(--py-arkaplan-soluk, rgba(0,0,0,0.02)) !important;
      border: 1px solid var(--py-kenarlik, #d9d9d9) !important;
      border-radius: 8px !important;
      min-height: 34px;
      transition: border-color 0.25s ease, background 0.25s ease, box-shadow 0.25s ease;
    }

    .input-wrap:hover ::ng-deep .ant-select-selector {
      border-color: var(--py-kenarlik-hover, #b3b3b3) !important;
      background: var(--py-arkaplan-hover, #f5f5f5) !important;
    }

    .input-wrap:focus-within ::ng-deep .ant-select-selector {
      border-color: var(--py-kenarlik-aktif, #1890ff) !important;
      background: var(--py-input-arka, #fff) !important;
      box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1) !important;
    }

    .input-wrap ::ng-deep nz-input-number {
      width: 100%;
    }

    .input-wrap ::ng-deep .ant-input-number {
      background: var(--py-arkaplan-soluk, rgba(0,0,0,0.02));
      border: 1px solid var(--py-kenarlik, #d9d9d9);
      border-radius: 8px;
      transition: border-color 0.25s ease, background 0.25s ease, box-shadow 0.25s ease;
    }

    .input-wrap:hover ::ng-deep .ant-input-number {
      border-color: var(--py-kenarlik-hover, #b3b3b3);
      background: var(--py-arkaplan-hover, #f5f5f5);
    }

    .input-wrap:focus-within ::ng-deep .ant-input-number {
      border-color: var(--py-kenarlik-aktif, #1890ff);
      background: var(--py-input-arka, #fff);
      box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
    }

    .input-wrap ::ng-deep .ant-picker {
      background: var(--py-arkaplan-hover, rgba(0,0,0,.02)) !important;
      border: 1px solid var(--py-kenarlik, rgba(0,0,0,.1)) !important;
      border-radius: 8px !important;
      min-height: 34px;
      width: 100% !important;
      font-size: 13px !important;
      transition: border-color .25s, box-shadow .25s, background .25s !important;
    }
    .input-wrap ::ng-deep .ant-picker input { font-size: 13px !important; color: var(--py-metin) !important; }

    .input-wrap:hover ::ng-deep .ant-picker {
      border-color: rgba(102,126,234,.3) !important;
      background: var(--py-arkaplan-hover, rgba(0,0,0,.01)) !important;
    }

    .input-wrap:focus-within ::ng-deep .ant-picker,
    .input-wrap ::ng-deep .ant-picker-focused {
      border-color: rgba(102,126,234,.5) !important;
      background: transparent !important;
      box-shadow: 0 0 0 2px rgba(102,126,234,.08) !important;
    }

    .durum-dot {
      display: inline-block;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      margin-right: 6px;
      vertical-align: middle;
    }

    @keyframes shimmer {
      0% { background-position: -200% center; }
      100% { background-position: 200% center; }
    }
  `],
})
export class SiparisEditComponent extends FikoYTKokBilesenComponent {
  lookup = inject(HmiLookupService);
  private req = inject(FikoYTRequestService);
  parametreler = model<any>();
  projeler = signal<any[]>([]);

  constructor() {
    super();
    this.lookup.musterileriYukle();
    this.projeleriYukle();
  }

  p = computed(() => this.parametreler()?.ek || {});

  async projeleriYukle(arama?: string) {
    const pipe: any[] = [
      { $match: { silindi: { $ne: true } } },
      { $lookup: { from: 'hareketler', localField: 'veri.icerikler', foreignField: '_id', as: 'veri.icerikler' } },
      { $match: { $or: [{ 'veri.icerikler.ek._secilenSetId': { $regex: '^hmi\\.proje' } }, { 'veri.icerikler.ek.bilesen': { $regex: '^proje' } }] } },
      { $sort: { _id: -1 } }, { $limit: 30 }, { $project: { _id: 1, 'veri.icerikler': 1 } }
    ];
    try {
      const sonuc = await this.req.aggregate<any>('FikoYT.sistem.DB.hareketler.aggregate', pipe);
      this.projeler.set((sonuc || []).map((d: any) => {
        const ek = d.veri?.icerikler?.[0]?.ek || {};
        const ad = ek?.parametreler?.find((p: any) => p.ismi === 'projeAdi')?.degeri || ek?.projeAdi || '#' + String(d._id).slice(-4);
        return { _id: d._id, ad };
      }));
      this.CD.detectChanges();
    } catch { }
  }
  projeAra(terim: string) { this.projeleriYukle(terim); }

  sipDurumRenk(v: string): string {
    return DURUM_SECENEKLERI.find(d => d.deger === v)?.renk || '#faad14';
  }

  birimler = ['Adet', 'Kg', 'Ton', 'Metre', 'Takım'];
  paraBirimleri = ['TRY', 'USD', 'EUR'];
  durumSecenekleri = DURUM_SECENEKLERI;

  guncelle(alan: string, deger: any) {
    const params = this.parametreler();
    if (params?.ek) {
      params.ek[alan] = deger;
      this.parametreler.set({ ...params });
    }
  }
}
